// One document of the files list on a phone (< md): a card with the checkbox
// and status on top, the title (up to 3 lines), the meta line, signers as a
// list, the primary action full width, and "Peržiūrėti / Atsisiųsti / ⋯" below.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { FileRowSheet } from "./file-row-menu";
import {
  DISABLED_LINK,
  FOOTER_BUTTON,
  FileTitle,
  MetaLine,
  RelatedToggle,
  SignerList,
  StatusPillBadge,
  UploadSignedCopyInput,
} from "./file-row-parts";
import type { FileItem } from "./file-item";
import useFileRow, { type FilesListContext } from "./use-file-row";

type Props = {
  item: FileItem;
  ctx: FilesListContext;
  isChild?: boolean;
};

export default function FileCard({ item, ctx, isChild = false }: Props) {
  const { t } = useTranslation();
  const row = useFileRow(item, ctx, isChild);
  const [relatedOpen, setRelatedOpen] = useState(false);
  const relatedListId = `related-files-${item.id}`;

  return (
    <div
      className={clsx(
        "flex flex-col gap-2 rounded-xl border bg-white p-3",
        row.isSelected ? "border-indigo-500 ring-1 ring-indigo-500" : "border-zinc-200"
      )}
    >
      <div className="flex items-center gap-2">
        {row.isSelectable && (
          <input
            type="checkbox"
            className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={row.isSelected}
            onChange={row.toggleSelected}
            aria-label={`${t("select")}: ${row.title}`}
          />
        )}
        {row.status && <StatusPillBadge status={row.status} />}
        {row.isWithoutSigning && (
          <span className="text-xs text-zinc-500">{t("files_status_no_signing")}</span>
        )}
      </div>
      <p className="line-clamp-3 text-sm leading-snug">
        <FileTitle title={row.title} url={row.documentPageUrl} />
      </p>
      <MetaLine item={item} number={row.number} categoryTitle={row.categoryTitle} />
      {row.signers.length > 0 && (
        <SignerList
          signers={row.signers}
          onRemind={row.remind}
          remindingKey={row.remindingKey}
        />
      )}
      {row.related.length > 0 && (
        <div className="text-sm">
          <RelatedToggle
            count={row.related.length}
            expanded={relatedOpen}
            onToggle={() => setRelatedOpen((open) => !open)}
            controls={relatedListId}
          />
          {/* Always in the DOM, so the toggle's aria-controls points at it.
              No display utility here: it would override the hidden attribute. */}
          <div
            id={relatedListId}
            hidden={!relatedOpen}
            className="mt-2 space-y-2 border-l-2 border-zinc-200 pl-2"
          >
            {relatedOpen &&
              row.related.map((child) => (
                <FileCard key={child.id} item={child} ctx={ctx} isChild />
              ))}
          </div>
        </div>
      )}
      {row.primary && (
        <button
          type="button"
          className="w-full rounded-md bg-indigo-600 px-3 py-2.5 text-sm font-medium text-white"
          onClick={row.primary.run}
        >
          {row.primary.label}
        </button>
      )}
      <div className="flex gap-2 border-t border-zinc-100 pt-2">
        <button
          type="button"
          className={clsx(FOOTER_BUTTON, "disabled:opacity-40")}
          disabled={!row.canView}
          onClick={row.view}
        >
          {t("view")}
        </button>
        <a
          href={row.downloadUrl || undefined}
          target="_blank"
          rel="noreferrer"
          className={clsx(FOOTER_BUTTON, !row.downloadUrl && DISABLED_LINK)}
          aria-disabled={!row.downloadUrl}
        >
          {t("download")}
        </a>
        <FileRowSheet items={row.menuItems} ariaLabel={t("more")} />
      </div>
      {row.signedCopyUpload && (
        <UploadSignedCopyInput
          ref={row.signedCopyUpload.inputRef}
          onFile={row.signedCopyUpload.onFile}
        />
      )}
    </div>
  );
}
